import { IPublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ToastContextProvider, useToastContext } from './components/Core/ToastContext';
import { routeTree } from './routeTree.gen';
import { LicenseChecker } from './services/licenseChecker';
import { ErrorResponse500 } from './api/types.gen';

// Tanstack router

export type RouterContext = {
  msalInstance: IPublicClientApplication | undefined;
  licenseChecker: ReturnType<typeof LicenseChecker>;
};

// Set up a Router instance
// eslint-disable-next-line react-refresh/only-export-components
export const router = createRouter({
  routeTree,
  defaultPreload: false,
  defaultStaleTime: 0,
  context: {
    msalInstance: undefined,
    licenseChecker: LicenseChecker(),
  },
});

// Register things for typesafety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
export interface AppProps {
  msalInstance: IPublicClientApplication;
}

function AppWithQueryClient({ msalInstance }: Readonly<AppProps>) {
  const { showErrorToast } = useToastContext();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            const err = error as unknown as ErrorResponse500;
            showErrorToast(error.message, err.traceId, err.timestamp)
          },
        }),
        mutationCache: new MutationCache({
          onError: (error) => {
            const err = error as unknown as ErrorResponse500;
            showErrorToast(err.message, err.traceId, err.timestamp)
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: 0,
            gcTime: 0,
          },
        },
      }),
    [showErrorToast],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} context={{ msalInstance }} />
    </QueryClientProvider>
  );
}

function App({ msalInstance }: Readonly<AppProps>) {
  return (
    <FluentProvider theme={webLightTheme}>
      <MsalProvider instance={msalInstance}>
        <ToastContextProvider>
          <AppWithQueryClient msalInstance={msalInstance} />
        </ToastContextProvider>
      </MsalProvider>
    </FluentProvider>
  );
}

export default App;
